import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearSession, SESSION_EVENT } from '../lib/api';

// Where each role lands after signing in.
const DASHBOARD_BY_ROLE = {
  admin: '/admin',
  patient: '/patient',
  institution: '/institution',
  researcher: '/research',
};

const readStoredUser = () => {
  try {
    const raw = sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(readStoredUser);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    // Re-read on navigation so signing in or out updates the bar immediately.
    setUser(readStoredUser());
  }, [location]);

  const handleSignOut = () => {
    clearSession();
    setUser(null);
    navigate('/');
  };

  const dashboardPath = user ? (DASHBOARD_BY_ROLE[user.user_type] || '/research') : null;

  useEffect(() => {
    const sync = () => setUser(readStoredUser());
    const close = event => { if (event.key === 'Escape') setMenuOpen(false); };
    window.addEventListener(SESSION_EVENT, sync);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener(SESSION_EVENT, sync); window.removeEventListener('keydown', close); };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="text-white text-lg font-medium tracking-tight">
              HealthDB
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/researchers">Researchers</NavLink>
            <NavLink to="/patients">Patients</NavLink>
            <NavLink to="/institutions">Institutions</NavLink>
            <NavLink to="/explore">Try workspace</NavLink>
            <NavLink to="/developers">API</NavLink>
            <NavLink to="/collaborations">Collaborate</NavLink>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Link
                  to={dashboardPath}
                  className="text-white/60 hover:text-white text-sm transition-colors px-4 py-2"
                >
                  {user.name || 'Dashboard'}
                </Link>
                <button
                  onClick={handleSignOut}
                  className="px-5 py-2.5 border border-white/20 text-white text-xs font-medium uppercase tracking-wider hover:bg-white/5 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white/60 hover:text-white text-sm transition-colors px-4 py-2"
                >
                  Sign In
                </Link>
                <Link
                  to="/contact"
                  className="px-5 py-2.5 bg-white text-black text-xs font-medium uppercase tracking-wider hover:bg-gray-100 transition-colors"
                >
                  Request Pilot
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-white"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span
                className={`block h-px bg-white transition-all duration-300 ${
                  menuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block h-px bg-white transition-all duration-300 ${
                  menuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block h-px bg-white transition-all duration-300 ${
                  menuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-navigation"
        hidden={!menuOpen}
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          menuOpen ? 'max-h-screen bg-black/95 backdrop-blur-xl' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-8 space-y-6">
          <MobileNavLink to="/researchers">Researchers</MobileNavLink>
          <MobileNavLink to="/patients">Patients</MobileNavLink>
          <MobileNavLink to="/collaborations">Research collaborations</MobileNavLink>
          <MobileNavLink to="/institutions">Institutions</MobileNavLink>
          <MobileNavLink to="/platform">Roadmap</MobileNavLink>
          <MobileNavLink to="/explore">Try workspace</MobileNavLink>
          <MobileNavLink to="/developers">API for developers and agents</MobileNavLink>
          <MobileNavLink to="/about">About</MobileNavLink>
          <div className="pt-6 border-t border-white/10 space-y-4">
            {user ? (
              <>
                <MobileNavLink to={dashboardPath}>Dashboard</MobileNavLink>
                <button
                  onClick={handleSignOut}
                  className="block w-full px-5 py-3 border border-white/20 text-white text-sm font-medium uppercase tracking-wider text-center"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-white/60 hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/contact"
                  className="block px-5 py-3 bg-white text-black text-sm font-medium uppercase tracking-wider text-center"
                >
                  Request Pilot
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const NavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-4 py-2 text-sm transition-colors ${
        isActive
          ? 'text-white'
          : 'text-white/50 hover:text-white'
      }`}
    >
      {children}
    </Link>
  );
};

const MobileNavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`block text-2xl font-light ${
        isActive ? 'text-white' : 'text-white/50'
      }`}
    >
      {children}
    </Link>
  );
};

export default Navbar;
